import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Star, Download, MessageCircle, CheckCircle2, Circle,
    Image as ImageIcon, Video, FileText, Sparkles, Upload, Eye,
    ClipboardList, Users2, Clock, AlertCircle
} from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const TABS = [
    { id: "resumen", label: "Resumen" },
    { id: "material", label: "Material de clase" },
    { id: "tareas", label: "Tareas" },
    { id: "ia", label: "Aprendizaje IA" },
    { id: "progreso", label: "Mi progreso" },
];

export default function LessonDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [completing, setCompleting] = useState(false);
    const [tab, setTab] = useState("resumen");
    const [assignments, setAssignments] = useState([]);
    const [loadingAssignments, setLoadingAssignments] = useState(true);

    useEffect(() => {
        api.get(`/lessons/${id}`).then((r) => setLesson(r.data)).catch(() => {});
    }, [id]);

    async function loadAssignments() {
        setLoadingAssignments(true);
        try {
            const r = await api.get(`/assignments/lesson/${id}`);
            setAssignments(r.data || []);
        } catch {
            // silently ignore — student may not have access yet
        } finally {
            setLoadingAssignments(false);
        }
    }

    useEffect(() => { loadAssignments(); }, [id]);

    async function complete() {
        setCompleting(true);
        try {
            await api.post(`/lessons/${id}/complete`);
            const r = await api.get(`/lessons/${id}`);
            setLesson(r.data);
            toast.success("¡Lección marcada como completada!");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo completar");
        } finally {
            setCompleting(false);
        }
    }

    // Volver a la lista de lecciones de la unidad de esta lección.
    // No usamos navigate(-1): como las pantallas previas apilan entradas de
    // historial (cambios de pestaña, etc.), retroceder arma un ciclo entre listas.
    // Navegamos siempre a un destino explícito.
    function goBack() {
        const unitRef = lesson?.unit_id ?? lesson?.unit_number;
        if (unitRef != null) {
            navigate(`/alumno/lecciones?unit=${unitRef}`);
        } else {
            // Último recurso: la lista general de lecciones, nunca hacia atrás
            navigate("/alumno/lecciones");
        }
    }

    if (!lesson) return <div className="p-6 text-edu-muted">Cargando…</div>;

    const pendingCount = assignments.filter((a) => a.status === "pending").length;
    const hasMaterial = !!(lesson.summary_file_id || lesson.whiteboard_file_id || lesson.activities_file_id || lesson.youtube_url);

    return (
        <div className="fade-in" data-testid="lesson-detail">
            <div className="bg-edu-navy text-white px-6 md:px-10 py-7 relative">
                <button onClick={goBack} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-3">
                    <ArrowLeft size={16} /> Volver
                </button>
                <div className="flex items-center gap-3 text-sm text-white/70 mb-1">
                    <span className="edu-pill bg-edu-gold text-[#0A0A0A]">{lesson.subject}</span>
                    <span>{lesson.unit_name ? `${lesson.unit_name} · ` : ""}{lesson.grade}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">{lesson.title}</h1>
            </div>

            <div className="px-6 md:px-10 bg-white border-b border-edu sticky top-0 z-10">
                <div className="max-w-5xl mx-auto flex gap-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                tab === t.id
                                    ? "border-edu-navy text-edu-navy"
                                    : "border-transparent text-edu-muted hover:text-edu-text"
                            }`}
                        >
                            {t.label}
                            {t.id === "tareas" && pendingCount > 0 && (
                                <span className="ml-1.5 edu-pill bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5">
                                    {pendingCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-6 md:p-10 max-w-5xl mx-auto">
                {tab === "resumen" && (
                    <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-5 items-start">
                        <div className="flex flex-col gap-4">
                            <Section icon={<Star size={18} className="text-edu-gold" fill="currentColor" />} title="¿Qué aprenderás hoy?">
                                <p className="text-edu-body whitespace-pre-line">
                                    {lesson.learning_outcomes || "Tu profesor aún no ha publicado los objetivos de aprendizaje."}
                                </p>
                            </Section>

                            <Section icon={<FileText size={18} className="text-edu-navy" />} title="Lo que vimos en clase">
                                <p className="text-edu-body whitespace-pre-line">
                                    {lesson.description || "El profesor aún no ha subido el resumen."}
                                </p>
                            </Section>
                        </div>

                        <div className="flex flex-col gap-4">
                            <ProgressCard lesson={lesson} assignments={assignments} onGoTo={setTab} />
                            <AiCard lessonId={id} navigate={navigate} />
                        </div>
                    </div>
                )}

                {tab === "material" && (
                    <div className="flex flex-col gap-4 max-w-3xl">
                        <Section icon={<FileText size={18} className="text-edu-navy" />} title="Resumen de la lección">
                            {lesson.summary_file_id ? (
                                <a href={fileUrl(lesson.summary_file_id)} target="_blank" rel="noreferrer"
                                    className="edu-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm">
                                    <Download size={16} /> Descargar resumen PDF
                                </a>
                            ) : (
                                <p className="text-edu-muted text-sm">El profesor aún no ha subido el resumen.</p>
                            )}
                        </Section>

                        <Section icon={<ImageIcon size={18} className="text-edu-navy" />} title="Apuntes de clase">
                            {lesson.whiteboard_file_id ? (
                                <img src={fileUrl(lesson.whiteboard_file_id)} alt="pizarra" className="rounded-xl border border-edu max-w-full" />
                            ) : (
                                <div className="aspect-video w-full max-w-2xl rounded-xl bg-[#F8F9FB] border-2 border-dashed border-edu flex items-center justify-center text-edu-muted text-sm">
                                    Sin foto de pizarra subida
                                </div>
                            )}
                        </Section>

                        {lesson.youtube_url && (
                            <Section icon={<Video size={18} className="text-edu-navy" />} title="Video recomendado">
                                <div className="aspect-video w-full max-w-3xl rounded-xl overflow-hidden bg-black">
                                    <iframe src={lesson.youtube_url} title="Video"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen className="w-full h-full" />
                                </div>
                            </Section>
                        )}

                        <Section icon={<FileText size={18} className="text-edu-navy" />} title="Actividades para practicar">
                            {lesson.activities_file_id ? (
                                <a href={fileUrl(lesson.activities_file_id)} target="_blank" rel="noreferrer"
                                    className="edu-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm">
                                    <Download size={16} /> Descargar actividades
                                </a>
                            ) : (
                                <p className="text-edu-muted text-sm">No hay actividades disponibles aún.</p>
                            )}
                        </Section>

                        {!hasMaterial && (
                            <p className="text-edu-muted text-sm text-center py-6">
                                Tu profesor aún no ha subido material para esta lección.
                            </p>
                        )}
                    </div>
                )}

                {tab === "tareas" && (
                    <div className="max-w-3xl">
                        <TareasTab
                            assignments={assignments}
                            loading={loadingAssignments}
                            onChanged={loadAssignments}
                        />
                    </div>
                )}

                {tab === "ia" && (
                    <div className="max-w-3xl">
                        <AiCard lessonId={id} navigate={navigate} expanded />
                    </div>
                )}

                {tab === "progreso" && (
                    <div className="max-w-2xl flex flex-col gap-4">
                        <ProgressCard lesson={lesson} assignments={assignments} onGoTo={setTab} expanded />
                        <button
                            onClick={complete}
                            disabled={lesson.completed || completing}
                            className="edu-btn-accent w-full md:w-auto px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 justify-center"
                            data-testid="lesson-complete-button"
                        >
                            <CheckCircle2 size={18} />
                            {lesson.completed ? "Lección completada" : completing ? "Procesando…" : "Marcar lección como completada"}
                        </button>

                        <Section icon={<Users2 size={18} className="text-edu-navy" />} title="Compañeros de unidad">
                            <p className="text-edu-muted text-sm">
                                Esta sección conectará a futuro con Comunicados y dudas compartidas de la clase.
                            </p>
                        </Section>
                    </div>
                )}
            </div>

            <Link
                to="/alumno/tutor-ia"
                state={{ lesson_id: lesson.id }}
                className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-edu-gold text-[#0A0A0A] flex items-center justify-center shadow-xl hover:scale-110 transition-transform"
                title="Tutor IA"
            >
                <MessageCircle size={24} />
            </Link>
        </div>
    );
}

function Section({ icon, title, children }) {
    return (
        <section className="edu-card p-6">
            <h2 className="text-lg font-semibold mb-3 inline-flex items-center gap-2">
                {icon} {title}
            </h2>
            <div>{children}</div>
        </section>
    );
}

/* =================== Progreso ("Tu progreso") =================== */
function ProgressCard({ lesson, assignments, onGoTo, expanded }) {
    const allTasksDone = assignments.length > 0 && assignments.every((a) => a.status !== "pending");
    const hasTasks = assignments.length > 0;

    const items = [
        { key: "resumen", label: "Resumen leído", done: true },
        { key: "material", label: "Material revisado", done: !!(lesson.summary_file_id || lesson.whiteboard_file_id) },
        {
            key: "tareas",
            label: hasTasks ? (allTasksDone ? "Tareas completadas" : "Tarea pendiente") : "Sin tareas asignadas",
            done: hasTasks ? allTasksDone : true,
            goTo: "tareas",
        },
        { key: "ia", label: "Aprendizaje IA", done: !!lesson.ai_completed, goTo: "ia" },
    ];

    return (
        <div className="edu-card p-5">
            <p className="font-medium mb-3">Tu progreso</p>
            <div className="flex flex-col gap-2.5">
                {items.map((it) => (
                    <div
                        key={it.key}
                        onClick={() => it.goTo && onGoTo?.(it.goTo)}
                        className={`flex items-center gap-2 text-sm ${it.goTo ? "cursor-pointer hover:text-edu-navy" : ""} ${it.done ? "" : "text-edu-muted"}`}
                    >
                        {it.done ? (
                            <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                        ) : (
                            <Circle size={16} className="flex-shrink-0" />
                        )}
                        <span>{it.label}</span>
                    </div>
                ))}
            </div>
            {expanded && lesson.completed && (
                <div className="mt-4 pt-4 border-t border-edu flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 size={16} /> Lección marcada como completada
                </div>
            )}
        </div>
    );
}

/* =================== Aprendizaje IA ("Explicación personalizada") =================== */
function AiCard({ lessonId, navigate, expanded }) {
    return (
        <div className={`rounded-xl p-5 ${expanded ? "edu-card border-2 border-edu-gold/30 bg-gradient-to-br from-edu-gold/5 to-transparent" : "bg-edu-navy/5"}`}>
            <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-edu-navy" />
                <p className="font-medium text-edu-navy">Explicación personalizada</p>
            </div>
            <p className="text-sm text-edu-body mb-3 leading-relaxed">
                {expanded
                    ? "¿El material regular te resulta difícil? Accede a explicaciones 100% adaptadas a tu estilo de aprendizaje preferido (Visual, Auditivo, Lector o Kinestésico) junto a mapas conceptuales, timelines, flashcards y la asistencia directa de tu Guía IA."
                    : "Mapa conceptual, timeline y flashcards adaptados a tu estilo de aprendizaje."}
            </p>
            <button
                onClick={() => navigate(`/alumno/lecciones/${lessonId}/aprender`)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl ${
                    expanded
                        ? "edu-btn-primary w-full md:w-auto bg-gradient-to-r from-edu-navy to-indigo-950 text-white shadow-lg shadow-edu-navy/20 hover:opacity-95"
                        : "edu-btn-primary w-full"
                }`}
            >
                <Sparkles size={16} className="text-edu-gold fill-edu-gold" />
                {expanded ? "Ingresar a mi Tutoría Personalizada" : "Ver mi versión"}
            </button>
        </div>
    );
}

/* =================== Tareas ("Tareas de esta lección") =================== */
function TareasTab({ assignments, loading, onChanged }) {
    if (loading) {
        return <p className="text-edu-muted text-sm py-6">Cargando tareas…</p>;
    }

    if (assignments.length === 0) {
        return (
            <div className="edu-card p-10 flex flex-col items-center text-center gap-3">
                <ClipboardList size={36} className="text-edu-muted opacity-40" />
                <p className="font-medium text-edu-text">Sin tareas asignadas</p>
                <p className="text-edu-muted text-sm">Tu profesor no ha asignado tareas para esta lección.</p>
            </div>
        );
    }

    const total = assignments.length;
    const submitted = assignments.filter((a) => a.status === "submitted").length;
    const pending = total - submitted;

    return (
        <div className="flex flex-col gap-4">
            {/* Métricas resumen */}
            <div className="grid grid-cols-3 gap-3">
                <div className="edu-card p-4 text-center">
                    <p className="text-2xl font-bold text-edu-navy">{total}</p>
                    <p className="text-xs text-edu-muted mt-1">Total</p>
                </div>
                <div className="edu-card p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{submitted}</p>
                    <p className="text-xs text-edu-muted mt-1">Entregadas</p>
                </div>
                <div className="edu-card p-4 text-center">
                    <p className={`text-2xl font-bold ${pending > 0 ? "text-amber-600" : "text-edu-muted"}`}>{pending}</p>
                    <p className="text-xs text-edu-muted mt-1">Pendientes</p>
                </div>
            </div>

            {/* Cards de tareas */}
            {assignments.map((a) => (
                <AssignmentCard key={a.id} assignment={a} onChanged={onChanged} />
            ))}
        </div>
    );
}

function AssignmentCard({ assignment, onChanged }) {
    const [uploading, setUploading] = useState(false);
    const [textValue, setTextValue] = useState(assignment.my_submission?.text || "");

    const submitted = assignment.status === "submitted";
    const submission = assignment.my_submission;
    const isGraded = submission?.score !== null && submission?.score !== undefined;

    const scorePercent = isGraded ? Math.round((submission.score / assignment.max_score) * 100) : null;
    const scoreColor = scorePercent >= 70 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : scorePercent >= 50 ? "text-amber-700 bg-amber-50 border-amber-200"
        : "text-red-700 bg-red-50 border-red-200";

    async function handleFileChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/assignments/${assignment.id}/submit/file`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Entrega subida correctamente");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al subir la entrega");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    }

    async function handleSubmitText() {
        if (!textValue.trim()) {
            toast.error("Escribe tu respuesta antes de entregar");
            return;
        }
        setUploading(true);
        try {
            await api.post(`/assignments/${assignment.id}/submit/text`, { text: textValue.trim() });
            toast.success("Respuesta enviada");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al enviar la entrega");
        } finally {
            setUploading(false);
        }
    }

    const accept = assignment.delivery_type === "pdf" ? ".pdf"
        : assignment.delivery_type === "photo" ? "image/*" : undefined;

    return (
        <div className={`edu-card overflow-hidden ${!submitted ? "border-l-4 border-l-amber-400" : isGraded ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-blue-400"}`}>
            {/* Header de la card */}
            <div className="p-5 border-b border-edu/50">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                                !submitted
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : isGraded
                                        ? `${scoreColor}`
                                        : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}>
                                {!submitted ? (
                                    <><AlertCircle size={10} /> Pendiente</>
                                ) : isGraded ? (
                                    <><CheckCircle2 size={10} /> Calificado</>
                                ) : (
                                    <><Clock size={10} /> En revisión</>
                                )}
                            </span>
                            <span className="text-[11px] text-edu-muted bg-edu-soft px-2 py-0.5 rounded-full">
                                {deliveryLabel(assignment.delivery_type)}
                            </span>
                        </div>
                        <h3 className="text-sm font-semibold text-edu-text">{assignment.title}</h3>
                        {assignment.due_date && (
                            <p className="text-xs text-edu-muted mt-0.5 inline-flex items-center gap-1">
                                <Clock size={10} /> Entrega: {assignment.due_date}
                            </p>
                        )}
                    </div>

                    {/* Nota si está calificado */}
                    {isGraded && (
                        <div className={`flex-shrink-0 text-center px-3 py-2 rounded-xl border ${scoreColor}`}>
                            <p className="text-lg font-bold leading-none">{submission.score}</p>
                            <p className="text-[10px] mt-0.5">/ {assignment.max_score} pts</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Cuerpo — instrucciones */}
            {assignment.instructions && (
                <div className="px-5 py-3 bg-[#F8F9FB] border-b border-edu/50">
                    <p className="text-xs text-edu-muted leading-relaxed">{assignment.instructions}</p>
                </div>
            )}

            {/* Footer — acciones y estado */}
            <div className="px-5 py-4 flex flex-col gap-3">
                {submitted ? (
                    <>
                        {/* Estado post-entrega */}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" />
                                <span className="text-xs text-emerald-700 font-medium">
                                    Entregado el {new Date(submission.submitted_at).toLocaleDateString("es-PE", { day: "numeric", month: "long", year: "numeric" })}
                                </span>
                            </div>
                            {submission?.file_id && (
                                <a
                                    href={fileUrl(submission.file_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1.5 font-medium"
                                >
                                    <Eye size={12} /> Ver mi entrega
                                </a>
                            )}
                        </div>

                        {/* Nota por revisarse */}
                        {!isGraded && (
                            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
                                <Clock size={14} className="text-blue-500 flex-shrink-0" />
                                <p className="text-xs text-blue-700 font-medium">Nota pendiente — tu profesor aún no ha calificado esta entrega</p>
                            </div>
                        )}

                        {/* Feedback del profesor si existe */}
                        {isGraded && submission.feedback && (
                            <div className="bg-edu-soft rounded-xl px-4 py-3 border border-edu">
                                <p className="text-xs font-semibold text-edu-navy mb-1">Comentario del profesor</p>
                                <p className="text-xs text-edu-body">{submission.feedback}</p>
                            </div>
                        )}

                        {/* Corrección automática de gramática (IA) — instantánea, no reemplaza al profesor */}
                        {submission?.ai_grammar_report && (
                            <div className="bg-violet-50 rounded-xl px-4 py-3 border border-violet-200">
                                <p className="text-xs font-semibold text-violet-800 mb-1">
                                    Corrección automática de gramática
                                </p>
                                {submission.ai_grammar_report.has_errors ? (
                                    <>
                                        <p className="text-xs text-violet-700 mb-2">
                                            {submission.ai_grammar_report.error_count} error(es) encontrado(s). Revísalos y corrígelos tú mismo antes de la revisión del profesor.
                                        </p>
                                        <ul className="space-y-1.5">
                                            {(submission.ai_grammar_report.errors || []).map((err, i) => (
                                                <li key={i} className="text-xs text-violet-900 bg-white/60 rounded-lg px-2.5 py-1.5">
                                                    <span className="font-medium">"{err.excerpt}"</span>
                                                    <span className="text-violet-500"> — {err.type}</span>
                                                    <p className="text-violet-700 mt-0.5">{err.explanation}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <p className="text-xs text-violet-700">
                                        {submission.ai_grammar_report.summary || "No se encontraron errores relevantes de gramática."}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Comentarios según la rúbrica del profesor (IA) — no reemplaza la nota final */}
                        {submission?.ai_rubric_report && (submission.ai_rubric_report.criterios || []).length > 0 && (
                            <div className="bg-blue-50 rounded-xl px-4 py-3 border border-blue-200">
                                <p className="text-xs font-semibold text-blue-800 mb-1">Comentarios según la rúbrica</p>
                                {submission.ai_rubric_report.observaciones_generales && (
                                    <p className="text-xs text-blue-700 mb-2">{submission.ai_rubric_report.observaciones_generales}</p>
                                )}
                                <ul className="space-y-1.5">
                                    {submission.ai_rubric_report.criterios.map((c, i) => {
                                        const badge = c.cumple === "cumple"
                                            ? { label: "Cumple", cls: "bg-emerald-100 text-emerald-700" }
                                            : c.cumple === "parcial"
                                                ? { label: "Parcial", cls: "bg-amber-100 text-amber-700" }
                                                : { label: "No cumple", cls: "bg-red-100 text-red-700" };
                                        return (
                                            <li key={i} className="text-xs text-blue-900 bg-white/60 rounded-lg px-2.5 py-1.5">
                                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                    <span className="font-medium">{c.criterio}</span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                                                </div>
                                                <p className="text-blue-700">{c.comentario}</p>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </>
                ) : assignment.delivery_type === "text" ? (
                    <div className="flex flex-col gap-2">
                        <textarea
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                            rows={3}
                            placeholder="Escribe tu respuesta aquí…"
                            className="edu-input w-full text-sm"
                        />
                        <button
                            onClick={handleSubmitText}
                            disabled={uploading}
                            className="edu-btn-primary px-4 py-2 text-xs self-end disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                            <Upload size={13} />
                            {uploading ? "Enviando…" : "Entregar respuesta"}
                        </button>
                    </div>
                ) : (
                    <label className="edu-btn-primary px-4 py-2.5 text-sm cursor-pointer inline-flex items-center gap-2 self-start font-medium">
                        <Upload size={15} />
                        {uploading ? "Subiendo…" : "Subir entrega"}
                        <input type="file" accept={accept} className="hidden" disabled={uploading} onChange={handleFileChange} />
                    </label>
                )}
            </div>
        </div>
    );
}

function deliveryLabel(type) {
    if (type === "pdf") return "PDF";
    if (type === "photo") return "Foto";
    if (type === "text") return "Texto";
    return type;
}
